import { ListAllVehicule, TotalActiveVehicule } from '@/api/entities/vehicule';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createNewVehicule,
  getAllVehicules,
  getTotalActiveVehicules,
  getVehiculeById,
  listAllVehicules,
  updateManyVehicules,
  updateVehicule,
  importVehicule,
  getExistingVehicule,
} from '@/api/vehicule/vehicule';
import { VehiculeCheckFilterType } from '@/entities/vehicle';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { useAuth } from './auth.hooks';

interface RequestVehiculesProps<TSchema> {
  data: TSchema[];
}

export const useCreateVehicule = ({
  t,
  showDefaultSuccessMessage = true,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TVehiculeSchema) =>
      createNewVehicule(data, {
        createdByUserId: limitedRoleLoggedInUserId,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });

      if (showDefaultSuccessMessage) {
        notify(t('common:vehiculeCreatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetExistingVehicule = (query?: TVehiculeSchema) => {
  return useQuery({
    queryKey: ['getExistingVehicule', query],
    queryFn: () => getExistingVehicule(query),
  });
};

export const useListAllVehicules = (query?: ListAllVehicule) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllVehicules', query],
    queryFn: () =>
      listAllVehicules({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetAllVehicules = (
  filter: VehiculeCheckFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  return useQuery({
    queryKey: ['getAllVehicules', filter, page, sortBy, sortDirection],
    queryFn: () => getAllVehicules(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveVehicules = (query?: TotalActiveVehicule) => {
  return useQuery({
    queryKey: ['getTotalActiveVehicules', query],
    queryFn: () => getTotalActiveVehicules(query),
  });
};

export const useGetVehiculeById = (id: string) => {
  return useQuery({
    queryKey: ['getVehiculeById', id],
    queryFn: () => getVehiculeById(id),
    enabled: !!id,
  });
};

export const useUpdateVehicule = ({
  t,
  showDefaultSuccessMessage = true,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicule: TVehiculeSchema) => updateVehicule(vehicule),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeById', id] });

      if (showDefaultSuccessMessage) {
        notify(t('common:vehiculeUpdatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicule:', err.error);
    },
  });
};

export const useImportVehicles = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vehicule: TVehiculeSchema[]) => importVehicule(vehicule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeById'] });

      handleSuccess?.();

      notify(t('vehiculeImportSuccess'), NOTIFY.SUCCESS);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicules:', error);
    },
  });
};

export const useUpdateManyVehicules = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicules: TVehiculeSchema[]) =>
      updateManyVehicules(vehicules),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getVehiculeById', item?.id],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });

      notify(t('common:vehiculeUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicules:', error);
    },
  });
};

export const useGetVehiculesSelectOptions = (query?: ListAllVehicule) => {
  const { data: allVehicules, isLoading: isLoadingAllVehicules } =
    useListAllVehicules(query);

  const getAllVehiculesSelectOptions = () => {
    if (
      !isLoadingAllVehicules &&
      allVehicules &&
      allVehicules.data.length > 0
    ) {
      return allVehicules.data.map((group: TVehiculeSchema) => {
        const { id, brand, model } = group;
        const licensePlateNumber = group?.licensePlateNumber
          ? `${group?.licensePlateNumber} -`
          : '';
        const vin = group?.vin ? ` - ${group?.vin}` : '';

        return {
          label: `${licensePlateNumber} ${brand || ''} - ${model || ''} ${vin}`,
          value: id,
        };
      });
    }

    return undefined;
  };

  return {
    getAllVehiculesSelectOptions,
    isLoadingAllVehicules,
  };
};

export const useRequestVehicules = <TSchema extends FieldValues>({
  data,
}: RequestVehiculesProps<TSchema>) => {
  const [activateAllVehiculeRequests, setActivateAllVehiculeRequests] =
    useState(false);

  const [requestedVehicules, setRequestedVehicules] = useState<TSchema[]>([]);

  const { mutate: mutateVehicules, isPending: isPendingVehicules } =
    useUpdateManyVehicules();

  useEffect(() => {
    const filteredData = data?.filter((item) => item?.requestVehiculeCheck);

    if (filteredData?.length === data?.length) {
      setActivateAllVehiculeRequests(true);
    } else {
      setActivateAllVehiculeRequests(false);
    }

    setRequestedVehicules(filteredData);
  }, [data]);

  const handleModalConfirmRequestVehicules = () => {
    let itemList = [];

    if (activateAllVehiculeRequests) {
      itemList = data?.map((item) => ({
        ...item,
        requestVehiculeCheck: true,
      }));
    } else {
      itemList = data?.map((item) => ({
        ...item,
        requestVehiculeCheck: false,
      }));
    }

    setRequestedVehicules(itemList);
    mutateVehicules(itemList);
  };

  const handleRequestVehicule = (entry: TSchema) => {
    let itemList: TSchema[] = requestedVehicules;

    const existingItem = itemList?.find((item) => item?.id === entry?.id);

    if (existingItem) {
      itemList = itemList?.filter((item) => item?.id !== entry?.id);

      mutateVehicules([
        {
          ...existingItem,
          requestVehiculeCheck: false,
        },
      ]);
    } else {
      itemList = [
        ...itemList,
        {
          ...entry,
          requestVehiculeCheck: true,
        },
      ];

      mutateVehicules(itemList);
    }

    setRequestedVehicules(itemList);
  };

  return {
    activateAllVehiculeRequests,
    requestedVehicules,
    isPendingVehicules,
    setActivateAllVehiculeRequests,
    handleModalConfirmRequestVehicules,
    handleRequestVehicule,
  };
};
